import { Notify, throttle } from 'quasar'
import { useAuthStore } from 'src/stores/index.js'
import UsersService from 'src/services/users.js'
import { getFCMToken } from 'src/utils/fcm'

const RESPONSES = {
  201: { message: 'Created', route: false },
  200: { message: 'OK', route: false },
  204: { message: 'deleted', route: false },
  401: { message: 'Not authorized', route: 'login' },
  404: { message: 'Not found', route: { name: 'not-found' } },
  422: { message: 'Unprocessable entity', route: false },
  501: { message: 'Server Error', route: false },
  500: { message: 'Server Error', route: false }
}

const unauthorized = throttle((router) => {
  getFCMToken().then(token => {
    if (token) UsersService.unregisterDevice(token)
  }).catch(console.error)
  const authStore = useAuthStore()
  authStore.logout()
  router.push({ name: 'login' })
}, 2000)

// Licencia suspendida. El middleware del backend rechaza CADA request del usuario, no solo los del
// modulo de licencias, asi que este 403 no se puede tratar como uno de permisos: la reaccion
// correcta es cerrar sesion. Si se dejara pasar, el usuario se queda con la app abierta viendo
// errores en cada pantalla sin entender por que.
const LICENSE_SUSPENDED_CODE = 'LICENSE_SUSPENDED'
const LICENSE_SUSPENDED_FALLBACK = 'Tu licencia está suspendida. Contacta al administrador de tu organización.'

const isLicenseSuspended = (error) => {
  if (error.status !== 403) return false

  const headerCode = error.headers ? error.headers['x-binnacle-error-code'] : null

  return (error.data && error.data.code === LICENSE_SUSPENDED_CODE) || headerCode === LICENSE_SUSPENDED_CODE
}

const licenseSuspended = throttle((router, message) => {
  Notify.create({
    color: 'binnacle-red',
    message: message || LICENSE_SUSPENDED_FALLBACK,
    icon: 'lock',
    timeout: 8000
  })
  unauthorized(router)
}, 2000)

// Throttled: N requests fallando en ráfaga (pantallas que disparan varios fetch) apilaban un
// toast POR REQUEST tapando la UI. Un solo aviso cada 3s comunica lo mismo. Mismo patrón que
// `unauthorized` arriba. Exportado: los fetch de catálogo con suppressConnectionToast lo
// disparan manualmente cuando el fallback a cache tampoco tuvo datos (sin señal ≠ sin error).
export const notifyConnectionError = throttle(() => {
  Notify.create({
    color: 'binnacle-red',
    message: 'Error de conexión',
    icon: 'error',
    timeout: 3000
  })
}, 3000)

export default (response, router) => {
  // Cancelacion deliberada (AbortController/CancelToken): en axios 0.x un abort rechaza con un
  // `Cancel` (lib/cancel/Cancel.js: `{ message, __CANCEL__: true }`), que NO es `instanceof Error`
  // ni trae `isAxiosError` — asi que el guard de mas abajo no lo detecta y caia por el mismo
  // fallthrough de EXITO que el bug de timeout (mismo TypeError confuso en el caller). Se propaga
  // ANTES de ese guard y SIN notifyConnectionError: el usuario cancelo, no fallo la red. Caso real:
  // OccurrenceItemAddonsSection.vue usa AbortController para el debounce del comentario del addon.
  if (response && response.__CANCEL__) return Promise.reject(response)

  // Error de axios SIN respuesta del servidor: Network Error, o un timeout/abort (ECONNABORTED,
  // sin `.response`) — antes solo se cubria 'Network Error' explicito, asi que un timeout caia por
  // el fallthrough de EXITO de abajo (`return response`) y el caller reventaba con un TypeError
  // confuso al leer `response.data...` de un error en vez de propagar el motivo real. `isAxiosError`
  // es un flag que axios pone en TODOS sus errores (distingue de forma confiable un AxiosResponse
  // de exito, que nunca es instancia de Error).
  const isNetworkFailure = response?.message === 'Network Error' ||
    (response instanceof Error && response.isAxiosError && !response.response)
  if (isNetworkFailure) {
    // Los fetch con fallback propio (catálogos offline) marcan suppressConnectionToast en su
    // config de axios: sirven cache en vez de fallar, el toast solo metería ruido.
    if (!(response.config && response.config.suppressConnectionToast)) notifyConnectionError()
    return Promise.reject(response)
  }
  const error = response.response ? response.response : null
  if (error) {
    // suppressUnauthorizedRedirect: requests en BACKGROUND (p. ej. el job de recuperación
    // de fotos) no deben desloguear/redirigir al usuario por un 401 que él no provocó;
    // el caller maneja el 401 por su cuenta. Default (sin flag): comportamiento de siempre.
    const suppressRedirect = response.config && response.config.suppressUnauthorizedRedirect
    const current = router.currentRoute.value || router.currentRoute

    // Antes del 401 a proposito: es un 403, pero se resuelve cerrando sesion igual que aquel.
    if (isLicenseSuspended(error)) {
      if (current.name !== 'login') licenseSuspended(router, error.data && error.data.message)
    } else if (error.status === 401 && !suppressRedirect && current.name !== 'login' && !current.meta?.public) {
      unauthorized(router)
    } else {
      if (RESPONSES[error.status] !== undefined && ![404, 401].includes(error.status)) {
        console.error(error)
      }
    }
    return Promise.reject(error)
  }

  return response
}
